/**
 * Onboarding helpers — vibe class catalogue + validators for the 5-step quiz.
 *
 * Mirrors webapp/src/vibe/profile.ts ALLOWED_VIBE_CLASSES + DEFAULT_SIGNAL_TOGGLES
 * for client-side preview. Server is the source of truth on POST.
 */

import type { OnboardingRequest, SignalToggles, ToneVariant, VeilMode } from '../cloud/api';

export const ALLOWED_VIBE_CLASSES = [
    { name: 'calm_explorer',  label: 'Calm Explorer',  blurb: 'Quiet places, smooth pace, low drama.' },
    { name: 'night_driver',   label: 'Night Driver',   blurb: 'Late roads, atmospheric drives.' },
    { name: 'quiet_diner',    label: 'Quiet Diner',    blurb: 'Cosy spots over loud crowds.' },
    { name: 'weekend_hiker',  label: 'Weekend Hiker',  blurb: 'Trails, sunrise, breathing room.' },
    { name: 'gym_streaker',   label: 'Gym Streaker',   blurb: 'Routine, focus, gain.' },
    { name: 'coffee_snob',    label: 'Coffee Snob',    blurb: 'Specialty over chains, every time.' },
    { name: 'spirit_driver',  label: 'Spirit Driver',  blurb: 'Scenic, slightly adventurous, no rush.' },
    { name: 'foodie',         label: 'Foodie',         blurb: 'Hunting flavours and new menus.' },
    { name: 'scenic_cruiser', label: 'Scenic Cruiser', blurb: 'Sunset roads, pretty corners.' },
] as const;

export const TONE_VARIANTS: ReadonlyArray<{
    value: ToneVariant;
    label: string;
    blurb: string;
    preview: string;
}> = [
    { value: 'minimal',    label: 'Minimal',    blurb: 'Terse. Factual. No flourish.',
      preview: "Beirut Garden — calm, weekday-evenings." },
    { value: 'friendly',   label: 'Friendly',   blurb: 'Warm, casual — the default.',
      preview: "Beirut Garden's a calm pick for tonight — your kind of place." },
    { value: 'hype',       label: 'Hype',       blurb: 'Excited and energetic.',
      preview: "Yo — Beirut Garden tonight. Calm vibes, your jam." },
    { value: 'premium',    label: 'Premium',    blurb: 'Sophisticated, restrained.',
      preview: "I'd suggest Beirut Garden — measured atmosphere, suits your evening." },
    { value: 'funny',      label: 'Funny',      blurb: 'Playfully irreverent.',
      preview: "Beirut Garden tonight? Calm crowd, no chair-scraping. Solid life choice." },
    { value: 'analytical', label: 'Analytical', blurb: 'Data-forward, "because"-framed.',
      preview: "Beirut Garden: 88% Resonance. Three corroborating Echoes this week." },
    { value: 'gentle',     label: 'Gentle',     blurb: 'Soft, low-arousal, careful.',
      preview: "If you're up for it — Beirut Garden tends to be calm on evenings like this." },
];

export const DEFAULT_SIGNAL_TOGGLES: SignalToggles = {
    experience_sharing: false,
    music_vibe_sharing: 'off',
    presence_sharing: false,
    alert_participation: false,
    trend_signals: false,
    query_participation: true,
};

export function veilModeToTogglesPreset(mode: VeilMode): SignalToggles {
    const off: SignalToggles = { ...DEFAULT_SIGNAL_TOGGLES };
    switch (mode) {
        case 'full':
            return off;
        case 'soft':
            return {
                ...off,
                experience_sharing: true,
                trend_signals: true,
            };
        case 'circle':
            return {
                ...off,
                experience_sharing: true,
                trend_signals: true,
            };
        case 'open':
            return {
                ...DEFAULT_SIGNAL_TOGGLES,
                experience_sharing: true,
                presence_sharing: true,
                alert_participation: true,
                trend_signals: true,
            };
    }
}

export interface OnboardingDraft {
    pal_name: string;
    vibe_classes: string[];           // 1..3
    crowds: number;                   // -1..1
    novelty: number;
    pace: number;
    tone_preference: ToneVariant;
    privacy_mode: VeilMode;
    signal_toggles: SignalToggles;
}

export function emptyDraft(): OnboardingDraft {
    return {
        pal_name: 'Moji',
        vibe_classes: [],
        crowds: 0,
        novelty: 0,
        pace: 0,
        tone_preference: 'friendly',
        privacy_mode: 'soft',
        signal_toggles: veilModeToTogglesPreset('soft'),
    };
}

export interface DraftValidation {
    palName: string | null;
    vibeClasses: string | null;
    valid: boolean;
}

export function validateDraft(d: OnboardingDraft): DraftValidation {
    const palName = !d.pal_name.trim()
        ? 'Give your Pal a name.'
        : d.pal_name.trim().length > 31
            ? 'Name is too long (max 31).'
            : null;
    const vibeClasses = d.vibe_classes.length === 0
        ? 'Pick at least one vibe.'
        : d.vibe_classes.length > 3
            ? 'Pick at most three.'
            : null;
    return { palName, vibeClasses, valid: !palName && !vibeClasses };
}

export function draftToRequest(d: OnboardingDraft): OnboardingRequest {
    return {
        pal_name: d.pal_name.trim(),
        vibe_classes: d.vibe_classes,
        preferences: { crowds: d.crowds, novelty: d.novelty, pace: d.pace },
        tone_preference: d.tone_preference,
        privacy_mode: d.privacy_mode,
        signal_toggles: d.signal_toggles,
    };
}
